import ResultField from "@/components/Calculator/Components/ResultField/resultField.component";
import ResultTitle from "@/components/Calculator/Components/ResultTitle/resultTitle.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { isValidPositiveNumber } from "@/utils/calculators";
import { DamImage } from "@/utils/dam.utils";

type Props = {
    roomArea: number;
    tileArea: number;
    packageArea: number;
    mailParams: Record<string, string>;
};

const Result = ({ roomArea, tileArea, packageArea, mailParams }: Props) => {
    const { t } = useGranite();

    const nrOfTilesNeeded = () => {
        const nrOfTiles = Number(Math.ceil(roomArea / tileArea));

        return isValidPositiveNumber(nrOfTiles) ? nrOfTiles : 0;
    };
    // 5% loss
    const nrOfTilesNeeded5PercentLoss = (nrOfTilesNeeded: number) => {
        const nrOfTilesNeeded5PercentLoss = Number(nrOfTilesNeeded * 1.05);

        return isValidPositiveNumber(nrOfTilesNeeded5PercentLoss)
            ? nrOfTilesNeeded5PercentLoss
            : 0;
    };
    const tileArea5PercentLoss = (nrOfTilesNeeded5PercentLoss: number) => {
        const tileArea5PercentLoss = Number(
            nrOfTilesNeeded5PercentLoss * tileArea
        );

        return isValidPositiveNumber(tileArea5PercentLoss)
            ? tileArea5PercentLoss
            : 0;
    };
    const nrOfBoxes5PercentLoss = (tileArea5PercentLoss: number) => {
        const nrOfBoxes5PercentLoss = Number(
            Math.ceil(tileArea5PercentLoss / packageArea)
        );

        return isValidPositiveNumber(nrOfBoxes5PercentLoss)
            ? nrOfBoxes5PercentLoss
            : 0;
    };
    // 10% loss
    const nrOfTilesNeeded10PercentLoss = (nrOfTilesNeeded: number) => {
        const nrOfTilesNeeded10PercentLoss = Number(nrOfTilesNeeded * 1.1);

        return isValidPositiveNumber(nrOfTilesNeeded10PercentLoss)
            ? nrOfTilesNeeded10PercentLoss
            : 0;
    };
    const tileArea10PercentLoss = (nrOfTilesNeeded10PercentLoss: number) => {
        const tileArea10PercentLoss = Number(
            nrOfTilesNeeded10PercentLoss * tileArea
        );

        return isValidPositiveNumber(tileArea10PercentLoss)
            ? tileArea10PercentLoss
            : 0;
    };
    const nrOfBoxes10PercentLoss = (tileArea10PercentLoss: number) => {
        const nrOfBoxes10PercentLoss = Number(
            Math.ceil(tileArea10PercentLoss / packageArea)
        );

        return isValidPositiveNumber(nrOfBoxes10PercentLoss)
            ? nrOfBoxes10PercentLoss
            : 0;
    };
    // glue
    const glueNeededFloor = () => {
        const glueNeededFloor = roomArea * 3;

        return isValidPositiveNumber(glueNeededFloor) ? glueNeededFloor : 0;
    };
    const glueNeededWall = () => {
        const glueNeededWall = roomArea * 2;

        return isValidPositiveNumber(glueNeededWall) ? glueNeededWall : 0;
    };
    // joint cement
    const jointCementNeededFloor = () => {
        const jointCementNeededFloor = roomArea / 2;

        return isValidPositiveNumber(jointCementNeededFloor)
            ? jointCementNeededFloor
            : 0;
    };
    const jointCementNeededWall = () => {
        const jointCementNeededWall = roomArea / 2;

        return isValidPositiveNumber(jointCementNeededWall)
            ? jointCementNeededWall
            : 0;
    };

    return (
        <div className={"mt-4"}>
            <ResultTitle mailParams={mailParams} />
            <Row>
                <ResultField
                    copy={t("calculator.tileCalculator.roomArea")}
                    value={roomArea.toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                    lg={3}
                />
                <ResultField
                    copy={t("calculator.tileCalculator.tileArea")}
                    value={tileArea.toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                    lg={3}
                />
            </Row>
            <Row>
                <Col
                    sm={6}
                    className={"mt-4"}
                >
                    <Row>
                        <Col xs={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.amountTilesClassic"
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            xs={12}
                            md={6}
                        >
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfTiles"
                                    )}
                                    value={nrOfTilesNeeded5PercentLoss(
                                        nrOfTilesNeeded()
                                    ).toFixed(0)}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.areaOfTiles"
                                    )}
                                    value={tileArea5PercentLoss(
                                        nrOfTilesNeeded5PercentLoss(
                                            nrOfTilesNeeded()
                                        )
                                    ).toFixed(2)}
                                    unit={"m"}
                                    sup={2}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfBoxes"
                                    )}
                                    value={nrOfBoxes5PercentLoss(
                                        tileArea5PercentLoss(
                                            nrOfTilesNeeded5PercentLoss(
                                                nrOfTilesNeeded()
                                            )
                                        )
                                    ).toFixed(0)}
                                />
                            </Row>
                        </Col>
                        <Col
                            xs={12}
                            md={6}
                        >
                            <img
                                className={"my-2 w-100"}
                                src={DamImage.calculator.tileClassic()}
                                alt={"tegel-klassiek.jpg"}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col
                    sm={6}
                    className={"mt-4"}
                >
                    <Row>
                        <Col xs={12}>
                            <h4 className="d-sm-block">
                                {t("calculator.tileCalculator.amountTiles")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            xs={12}
                            md={6}
                        >
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfTiles"
                                    )}
                                    value={nrOfTilesNeeded10PercentLoss(
                                        nrOfTilesNeeded()
                                    ).toFixed(0)}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.areaOfTiles"
                                    )}
                                    value={tileArea10PercentLoss(
                                        nrOfTilesNeeded10PercentLoss(
                                            nrOfTilesNeeded()
                                        )
                                    ).toFixed(2)}
                                    unit={"m"}
                                    sup={2}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfBoxes"
                                    )}
                                    value={nrOfBoxes10PercentLoss(
                                        tileArea10PercentLoss(
                                            nrOfTilesNeeded10PercentLoss(
                                                nrOfTilesNeeded()
                                            )
                                        )
                                    ).toFixed(0)}
                                />
                            </Row>
                        </Col>
                        <Col
                            xs={12}
                            md={6}
                        >
                            <img
                                className={"my-2 w-100"}
                                src={DamImage.calculator.tileDiagonal()}
                                alt={"tegel-overhoeks.jpg"}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col
                    sm={6}
                    className={"mt-4"}
                >
                    <Row>
                        <Col xs={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.glueAndJoiningForFloorTiles"
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.tileCalculator.AmountOfGlue")}
                            value={glueNeededFloor().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t(
                                "calculator.tileCalculator.AmountOfJoining"
                            )}
                            value={jointCementNeededFloor().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                </Col>
                <Col
                    sm={6}
                    className={"mt-4"}
                >
                    <Row>
                        <Col xs={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.glueAndJoiningForWallTiles"
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.tileCalculator.AmountOfGlue")}
                            value={glueNeededWall().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t(
                                "calculator.tileCalculator.AmountOfJoining"
                            )}
                            value={jointCementNeededWall().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default Result;
